/**
 * The 32 printable ASCII punctuation marks — the set that **satisfies** the special-character
 * requirement (FR-022).
 *
 * It is **not** a whitelist of permitted characters. FR-022b is explicit that everything else is
 * allowed too; a space or an accented letter simply does not count as special. There is no
 * disallowed-character rule anywhere in this policy, which is why `PasswordRuleId` below has no member
 * that could express one.
 */
export const PASSWORD_SPECIAL_CHARACTERS = "!\"#$%&'()*+,-./:;<=>?@[\\]^_`{|}~";

/** Inclusive, and measured in **UTF-8 bytes** — see `countPasswordBytes`. */
export const PASSWORD_MIN_BYTES = 8;
export const PASSWORD_MAX_BYTES = 72;

/**
 * The rules a password can fail. An `as const` union rather than an enum (Configuration Principle III),
 * and deliberately closed: there is no `disallowedCharacter` member, so FR-022b holds by construction
 * rather than by remembering not to add one.
 */
export const PASSWORD_RULE_IDS = [
  "length",
  "uppercase",
  "lowercase",
  "digit",
  "special"
] as const;

export type PasswordRuleId = (typeof PASSWORD_RULE_IDS)[number];

export interface PasswordPolicyResult {
  /** Rules this value fails, in `PASSWORD_RULE_IDS` order. Empty when the policy is satisfied. */
  failedRules: PasswordRuleId[];
  isPolicyMet: boolean;
  /** The two fields differ. Reported **separately** from `failedRules` — FR-022 requires it. */
  isMismatched: boolean;
  /** Both halves pass. The only state a confirm action may act on. */
  isValid: boolean;
}

/**
 * Length in **UTF-8 bytes**, never `value.length`.
 *
 * 72 is bcrypt's truncation limit and **bcrypt counts bytes**. A 72-*character* password containing
 * accents exceeds it, so the service would silently truncate it and the stored credential would differ
 * from what the user typed — a login that fails for no visible reason. Most likely to bite in `fr-CA`,
 * where accented characters cost two bytes each.
 */
export const countPasswordBytes = (value: string) =>
  new TextEncoder().encode(value).length;

const hasUppercase = (value: string) => /[A-Z]/.test(value);
const hasLowercase = (value: string) => /[a-z]/.test(value);
const hasDigit = (value: string) => /[0-9]/.test(value);

/*
 * Membership test by character rather than by regex character class. A `[...]` class containing the
 * full 32-mark set needs `]`, `\`, `^`, and `-` escaped in the right positions, and getting one wrong
 * silently drops members — `includes` cannot.
 */
const hasSpecial = (value: string) =>
  value
    .split("")
    .some(character => PASSWORD_SPECIAL_CHARACTERS.includes(character));

/**
 * Validates a new password and its confirmation against the policy confirmed on 2026-08-17 (FR-022).
 *
 * A pure function: no state, no side effects, no logging — FR-051 forbids a password value reaching
 * any log, analytics event, or storage, and the cheapest way to guarantee that here is for this
 * function to do nothing but compute.
 *
 * **Rule violations and a mismatch are distinct results** (FR-022): they are separate fields so the
 * dialog can report "does not meet the requirements" and "passwords do not match" as different
 * messages, and can report both at once.
 *
 * **Do not reproduce the legacy CC6 copy** ("at least 8 characters long & contain at least one
 * number") anywhere near this — it understates the policy and would tell a user their password is
 * acceptable when the service will refuse it.
 */
export const validatePassword = (
  password: string,
  confirmation: string
): PasswordPolicyResult => {
  const byteLength = countPasswordBytes(password);

  const failedRules = PASSWORD_RULE_IDS.filter(rule => {
    switch (rule) {
      case "length":
        return (
          byteLength < PASSWORD_MIN_BYTES || byteLength > PASSWORD_MAX_BYTES
        );
      case "uppercase":
        return !hasUppercase(password);
      case "lowercase":
        return !hasLowercase(password);
      case "digit":
        return !hasDigit(password);
      case "special":
        return !hasSpecial(password);
      default:
        return false;
    }
  });

  const isPolicyMet = failedRules.length === 0;
  /*
   * Two empty fields are not a mismatch — they are unfilled, and the policy failure already says so.
   * Calling that a mismatch would show "passwords do not match" over an untouched form.
   */
  const isMismatched = password !== confirmation;

  return {
    failedRules: [...failedRules],
    isPolicyMet,
    isMismatched,
    isValid: isPolicyMet && !isMismatched
  };
};
